import { ArrowLeft, Loader2, CheckCircle } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { useCreatePayment } from '@/hooks/useCpn'
import { CpnBeneficiaryUser } from '@/types/cpn'
import { TransferData } from '@/types/payment'
import { getPaymentMethodInfo } from '@/utils/cpn'
import { logger } from '@/utils/logger'
import { savePaymentPreference } from '@/utils/paymentPreferences'

interface ReviewDetailsScreenProps {
  transferData: TransferData
  onNext: () => void
  onBack: () => void
  onChangePaymentMethod?: () => void
  onChangeTransferDetails?: () => void
  onChangeRecipientDetails?: () => void
  onChangeScheduleDetails?: () => void
  beneficiary?: CpnBeneficiaryUser
  userId?: string
}

const ReviewDetailsScreen = ({
  transferData,
  onNext,
  onBack,
  onChangeTransferDetails,
  onChangeRecipientDetails,
  beneficiary,
  userId,
}: ReviewDetailsScreenProps) => {
  const { toast } = useToast()
  const [paymentCreated, setPaymentCreated] = useState(false)

  const createPaymentMutation = useCreatePayment()
  const isDirectWire = transferData.paymentMethod === 'WIRE'
  const paymentMethodInfo = getPaymentMethodInfo(transferData.paymentMethod)

  const handleCreatePayment = async () => {
    if (!beneficiary) {
      toast({
        title: 'Missing recipient details',
        description: 'Please go back and complete recipient information.',
        variant: 'destructive',
      })
      return
    }

    // Get the stored quote ID from session storage
    const quoteId = sessionStorage.getItem('currentQuoteId')
    if (!quoteId) {
      toast({
        title: 'Missing quote',
        description: 'Please go back and regenerate a quote.',
        variant: 'destructive',
      })
      return
    }

    try {
      const result = await createPaymentMutation.mutateAsync({
        quoteId,
        beneficiaryRefId: beneficiary.uuid,
        reasonForPayment: 'PMT022', // Default payment reason
        useCase: 'B2B',
      })

      setPaymentCreated(true)

      toast({
        title: 'Payment initiated',
        description: 'Your payment has been successfully initiated.',
      })

      // Store payment ID for status tracking
      sessionStorage.setItem('currentPaymentId', result.paymentId)

      // Save payment preferences for future use
      savePaymentPreference(userId, {
        receiveCountry: transferData.receiveCountry,
        receiveCurrency: transferData.receiveCurrency,
        paymentMethod: transferData.paymentMethod,
      })

      // Proceed to next screen after a brief delay
      setTimeout(() => {
        onNext()
      }, 2000)
    } catch (error) {
      logger.error('Failed to create payment', error)
      toast({
        title: 'Payment failed',
        description: 'Failed to initiate payment. Please try again.',
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="h-[600px] flex flex-col">
      <div className="p-4 space-y-3 flex-1 flex flex-col">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100">
            Review details
          </h1>
        </div>

        <div className="flex-1 space-y-3">
          {/* Payment Method - No change button */}
          <div className="space-y-2">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Payment method
            </h2>
            <div className="flex items-center space-x-3">
              <div
                className={`w-8 h-6 rounded flex items-center justify-center ${
                  isDirectWire
                    ? 'bg-blue-600 dark:bg-blue-500'
                    : 'bg-green-600 dark:bg-green-500'
                }`}
              >
                <span className="text-white text-xs font-bold">
                  {paymentMethodInfo.icon}
                </span>
              </div>
              <span className="text-gray-900 dark:text-gray-100 text-sm">
                {paymentMethodInfo.name}
              </span>
            </div>
          </div>

          {/* Transfer Details */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Transfer details
              </h2>
              <button
                onClick={onChangeTransferDetails}
                className="text-gray-600 dark:text-gray-400 underline hover:text-gray-800 dark:hover:text-gray-200 transition-colors text-sm"
              >
                Change
              </button>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400 text-sm">
                  You send exactly
                </span>
                <span className="text-gray-900 dark:text-gray-100 font-medium text-sm">
                  {transferData.sendAmount} {transferData.sendCurrency}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400 text-sm">
                  Total fees (included)
                </span>
                <span className="text-gray-900 dark:text-gray-100 font-medium text-sm">
                  13.11 {transferData.sendCurrency}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400 text-sm">
                  Total amount we'll convert
                </span>
                <span className="text-gray-900 dark:text-gray-100 font-medium text-sm">
                  986.89 {transferData.sendCurrency}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400 text-sm">
                  Guaranteed rate (47 hours)
                </span>
                <span className="text-gray-900 dark:text-gray-100 font-medium text-sm">
                  1 {transferData.sendCurrency} = 0.7266{' '}
                  {transferData.receiveCurrency}
                </span>
              </div>

              <div className="flex justify-between border-t dark:border-gray-700 pt-2">
                <span className="text-gray-600 dark:text-gray-400 text-sm">
                  Recipient gets
                </span>
                <span className="text-gray-900 dark:text-gray-100 font-medium text-sm">
                  717.03 {transferData.receiveCurrency}
                </span>
              </div>
            </div>
          </div>

          {/* Recipient Details */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Recipient details
              </h2>
              <button
                onClick={onChangeRecipientDetails}
                className="text-gray-600 dark:text-gray-400 underline hover:text-gray-800 dark:hover:text-gray-200 transition-colors text-sm"
              >
                Change
              </button>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400 text-sm">
                  Account holder name
                </span>
                <span className="text-gray-900 dark:text-gray-100 font-medium text-sm">
                  {beneficiary
                    ? `${beneficiary.firstName} ${beneficiary.lastName}`
                    : 'Not specified'}
                </span>
              </div>

              {beneficiary?.city && beneficiary?.country && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400 text-sm">
                    Location
                  </span>
                  <span className="text-gray-900 dark:text-gray-100 font-medium text-sm">
                    {beneficiary.city}, {beneficiary.country}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={onBack}
              className="flex-1 flex items-center justify-center space-x-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 hover:border-yellow-200 dark:hover:border-yellow-700 transition-colors h-10 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </Button>

            <Button
              onClick={paymentCreated ? onNext : handleCreatePayment}
              disabled={createPaymentMutation.isPending}
              className="flex-1 bg-black dark:bg-gray-800 text-white hover:bg-gray-800 dark:hover:bg-gray-700 font-medium transition-colors h-10 text-sm flex items-center justify-center space-x-2"
            >
              {createPaymentMutation.isPending && (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
              {paymentCreated && <CheckCircle className="w-4 h-4" />}
              <span>
                {createPaymentMutation.isPending
                  ? 'Processing...'
                  : paymentCreated
                    ? 'Payment Created!'
                    : 'Send Payment'}
              </span>
            </Button>
          </div>
        </div>
      </div>

      {/* Powered by Paytrie */}
      <div className="text-center p-2 border-t border-gray-100 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
          Powered by Paytrie
        </p>
        <img
          src="/paytrie-logo.png"
          alt="Paytrie"
          className="h-6 w-auto mx-auto"
        />
      </div>
    </div>
  )
}

export default ReviewDetailsScreen
